import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Phone, Eye, EyeOff, Package } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import toast from '../utils/toast';
import { GoogleLogin } from '@react-oauth/google';
import { PhoneLogin } from '../components/auth/PhoneLogin';

const CustomerLogin: React.FC = () => {
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  // const [email, setEmail] = useState('n.wright96@gmail.com');
  // const [email, setEmail] = useState('gregphillip@gmail.com');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // const [password, setPassword] = useState('customer123');
  // const [password, setPassword] = useState('GregPhillip');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem('remember_email');
    if (saved) {
      setEmail(saved);
      setRememberMe(true);  // or false, your choice — reflects that this was remembered
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const identifier = method === 'email' ? email : phone;
      await login(identifier, password, rememberMe);
      navigate('/products');
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
            <Package className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-600 mt-2">Sign in to your Wright Tech account</p>
        </div>

        {/* Social Login */}<div className="space-y-3 mb-6">
          <GoogleLogin
            onSuccess={(credentialResponse) => {
              loginWithGoogle(credentialResponse.credential!, rememberMe);
              navigate('/products');
            }}
            onError={() => toast.error('Google login failed')}
            useOneTap
            shape="pill"
            size="large"
            ux_mode="popup"
            text="continue_with"
            width="100%" // Makes it full width
          />

          {/* TikTok button (placeholder) */}
          <button
            className="w-full flex items-center justify-center gap-3 bg-black text-white rounded-lg py-3 px-4 hover:bg-gray-900"
            onClick={() => toast.info('TikTok login coming soon')}
          >
            <FaTiktok className="h-5 w-5" />
            <span className="font-medium">Continue with TikTok</span>
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-transparent text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Method Toggle */}
          <div className="flex mb-6 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setMethod('email')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${method === 'email' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              <Mail className="inline h-4 w-4 mr-2" />
              Email
            </button>
            <button
              onClick={() => setMethod('phone')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${method === 'phone' ? 'bg-white shadow' : 'text-gray-600'}`}
            >
              <Phone className="inline h-4 w-4 mr-2" />
              Phone
            </button>
          </div>

          {method === 'email' ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Remember me</span>
                  </label>
                  <a href="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </>) : (
            <PhoneLogin />
          )
          }

          {method === 'email' && (<>
            <p className="mt-6 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <a href="/signup" className="font-medium text-blue-600 hover:text-blue-500">
                Sign up
              </a>
            </p>

            {/* <p className="mt-4 text-center text-sm text-gray-600">
              Are you staff?{' '}
              <a href="/staff/login" className="font-medium text-blue-600 hover:text-blue-500">
                Staff login here
              </a>
            </p> */}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;